import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { DomSanitizer } from "@angular/platform-browser";
import { forkJoin, Observable, of } from "rxjs";
import { filter, map, mergeMap } from "rxjs/operators";
import { NoticesSearchParams } from "src/app/feature/notices-communications/model/noticesSearchParms";
import { endpoints, environment } from "src/environments/environment";
import { DateUtilService } from "../date-util/date-util.service";
import { UrlUtilService } from "../url-util/url-util.service";
import { FaqCategories } from "./model/faqCategories";
import { FaqRequest } from "./model/faqRequest";
import { PageInfo } from "./model/pageInfo";
import { Utility } from "../../utility/utility";
import { ProfileManagementService } from "../store-management/profile-management/profile-management.service";
import { InformativeNote } from "src/app/feature/home/informative-notes/model/informative-note.model";

@Injectable({
  providedIn: "root",
})
export class CmsService {
  private INFORMATIVE_NOTES_URL = endpoints.informative_notes;
  private HOME_CARDS_URL = endpoints.home_cards_url;
  private CONTACTS_URL = endpoints.contacts_url;
  private VISOR_URL = endpoints.visor_url;
  private FORMS_URL = endpoints.forms;
  private PRODUCTS_URL = endpoints.products;
  private FAQ_URL = endpoints.faq_url;
  private FAQ_CATEGORIES = endpoints.faq_categories;
  private PAGE_DESCRIPTION_URL = endpoints.page_description;
  private MANUALS_URL = endpoints.manuals;
  private NOTICES_COMMUNICATION_URL = endpoints.notices_communications;
  private NOTICES_COMMUNICATION_EXPORT_URL =
    endpoints.notices_communications_export;
  private SALESFORCE_CATEGORY_DETAIL_URL =
    endpoints.salesforce_category_details;
  private CENTER_SERVICE_FORD_URL = endpoints.service_centers_ford;
  private CONTENT_CMS = endpoints.get_content_cms;
  private MAINTENANCE_BANNER_URL = endpoints.maintenance_banner;

  // profile = this.profileManagementService.getProfileByStore()

  constructor(
    private httpClient: HttpClient,
    private urlUtil: UrlUtilService,
    private dateUtil: DateUtilService,
    private sanitaze: DomSanitizer,
    private profileManagementService: ProfileManagementService
  ) {}

  getHomeCards(): Observable<any> {
    const customerId = Utility.getCustomerId();
    const role = Utility.getCurrentRole();
    const customerReference = Utility.getCustomerReference();
    const exclusionFunctionalityCode = this.profileManagementService.getProfileByStore().exclusionFunctionality;
    return this.flatObservable(this.httpClient.post(this.HOME_CARDS_URL, { customerId, role, customerReference, exclusionFunctionalityCode}));
  }

  getContacts(customerReference: string): Observable<any> {
    const customerId = Utility.getCustomerId();
    const role = Utility.getCurrentRole();
    return this.flatObservable(this.httpClient.post(`${this.CONTACTS_URL}?codiceCapogruppo=${customerReference}`, { customerId, role, customerReference }));
  }

  getFaq(pageInfo: PageInfo, faqRequest: FaqRequest): Observable<any> {
    const customerId = Utility.getCustomerId();
    const role = Utility.getCurrentRole();
    const customerReference = Utility.getCustomerReference();

    return this.httpClient.post(
      `${this.FAQ_URL}?${this.urlUtil.convertObjectToSearchParam(pageInfo)}`,
      { ...faqRequest, customerId, role, customerReference }
    );
  }

  getForms(): Observable<any> {

    const customerId = Utility.getCustomerId();
    const role = Utility.getCurrentRole();
    const customerReference = Utility.getCustomerReference();

    return this.flatObservable(this.httpClient.post(this.FORMS_URL, { customerId, role, customerReference }));
    //return this.flatObservable(this.httpClient.get(this.FORMS_URL));
  }


  getProducts(): Observable<any> {

    const customerId = Utility.getCustomerId();
    const role = Utility.getCurrentRole();
    const customerReference = Utility.getCustomerReference();

    return this.flatObservable(this.httpClient.post(this.PRODUCTS_URL, { customerId, role, customerReference }));
    //return this.flatObservable(this.httpClient.get(this.FORMS_URL));
  }

  getVisor(pageName: string, codiceCapogruppo: string): Observable<any> {

    const customerId = Utility.getCustomerId();
    const role = Utility.getCurrentRole();
    const customerReference = Utility.getCustomerReference();

    return this.flatObservable(
      this.httpClient.post(`${this.VISOR_URL}?PageName=${pageName}&codiceCapogruppo=${codiceCapogruppo}`, { customerId, role, customerReference })
    ).pipe(
      map((resp: any) =>
        resp.filter((el: any) => {
          const { dateEnd, dateStart } = el;

          if (
            (!dateEnd || dateEnd === "") &&
            (!dateStart || dateStart === "")
          ) {
            return true;
          }

          if (
            this.dateUtil.isDateBeforeToday(dateStart, "DD/MM/YYYY HH:mm") &&
            (this.dateUtil.isDateAfterToday(dateEnd, "DD/MM/YYYY HH:mm") ||
              dateEnd === "")
          ) {
            return true;
          }

          return false;
        })
      ),
      mergeMap((resp) => {
        return forkJoin(
          resp.map((item: any) =>
            this.getFile(item.image).pipe(
              map((file) => {
                item.image = file;
                return item;
              })
            )
          )
        );
      }),
      mergeMap((resp:any) => {
        return forkJoin(
          resp.map((item: any) =>
            this.getFile(item.mobileImage).pipe(
              map((file) => {
                item.mobileImage = file;
                return item;
              })
            )
          )
        );
      })
    );
  }

  getNotices(searchParams: NoticesSearchParams): Observable<any> {
    const customerId = Utility.getCustomerId();
    const role = Utility.getCurrentRole();
    const customerReference = Utility.getCustomerReference();
    return this.httpClient.post(
      `${
        this.NOTICES_COMMUNICATION_URL
      }?${this.urlUtil.convertObjectToSearchParam(searchParams)}`,
      { customerId, role, customerReference }
    );
  }

  getNoticesExcelExport(searchParams: NoticesSearchParams): Observable<any> {
    const customerId = Utility.getCustomerId();
    const role = Utility.getCurrentRole();
    const customerReference = Utility.getCustomerReference();
    return this.httpClient.post(
      `${
        this.NOTICES_COMMUNICATION_EXPORT_URL
      }?${this.urlUtil.convertObjectToSearchParam(searchParams)}`,
      { customerId, role, customerReference },
      { responseType: "blob" as "json" }
    );
  }

  getManuals(leasingCompany: any): Observable<any> {
    const customerId = Utility.getCustomerId();
    const role = Utility.getCurrentRole();
    const customerReference = Utility.getCustomerReference();
    return this.flatObservable(this.httpClient.post( `${this.MANUALS_URL}?leasingCompany=${leasingCompany}`, { customerId, role, customerReference }));
  }

  getPageDescription(PageName: string, category: string): Observable<any> {
    const customerId = Utility.getCustomerId();
    const role = Utility.getCurrentRole();
    const customerReference = Utility.getCustomerReference();

    return this.httpClient.post(
      `${this.PAGE_DESCRIPTION_URL}?${this.urlUtil.convertObjectToSearchParam({
        PageName,
        category,
      })}`,
      { customerId, role, customerReference }
    );
  }

  getFaqCategories(faqCategories: FaqCategories): Observable<any> {
    return this.httpClient.get(
      `${this.FAQ_CATEGORIES}?${this.urlUtil.convertObjectToSearchParam(
        faqCategories
      )}`
    );
  }

  getSalesForceCategoryDetail(): Observable<any> {
    return this.httpClient.get(this.SALESFORCE_CATEGORY_DETAIL_URL);
  }

  private flatObservable(observable: Observable<any>): Observable<any> {
    return observable.pipe(
      filter((resp: any[]) => !!resp),
      map((resp: any[]) => resp.map((el) => el?.acf))
    );
  }

  getFile(url: string): Observable<any> {
    return this.httpClient
      .get(
        `${this.CONTENT_CMS}?url=${url.replace('https://','http://')}`,
        {
          headers: { "Content-Type": "image/jpg" },
          responseType: "blob",
        }
      )
      .pipe(
        map((res) => {
          const objectURL = URL.createObjectURL(res);
          // return this.sanitaze.bypassSecurityTrustResourceUrl(objectURL);
          return objectURL;
        })
      );
  }

  getServiceCentersListFord(): Observable<any> {
    const customerId = Utility.getCustomerId();
    const role = Utility.getCurrentRole();
    const customerReference = Utility.getCustomerReference();
    return this.flatObservable(this.httpClient.post(this.CENTER_SERVICE_FORD_URL, { customerId, role, customerReference }));
  }

  getMaintenanceBanners(): Observable<any> {
    const headers = new HttpHeaders({
          'Ocp-Apim-Subscription-Key': environment.OcpApimSubscriptionKey,
          'x-ald-subscription-key': environment.xAldSubscriptionKey,
    })

    return this.flatObservable(
      this.httpClient.get(this.MAINTENANCE_BANNER_URL, { headers })
    );
  }

  // Note informative (sezione CMS "informative_disclaimer"). Ritorna l'array degli ACF.
  getInformativeNotes(): Observable<InformativeNote[]> {
    return this.flatObservable(
        this.httpClient.get(this.INFORMATIVE_NOTES_URL)
    );
  }
}
